import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtDMY, fmtTime, fmtMins } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { ChevronDown, ChevronRight, Gauge, Repeat } from "lucide-react";

export function EstimateEditsCard({ start, end }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["reports-estimate-edits", start, end],
    queryFn: async () => (await api.get(`/reports/estimate-edits?start=${start}&end=${end}`)).data,
    enabled: !!start && !!end,
  });
  const items = data?.items || [];
  return (
    <Card className="rounded-md border p-4" data-testid="estimate-edits-section">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="estimate-edits-toggle" className="flex w-full items-center gap-2 text-left">
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        <Gauge className="h-4 w-4 text-indigo-600" />
        <span className="font-display text-lg font-extrabold">{t("Estimate corrections")}</span>
        <span className="ml-1 rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold text-muted-foreground" data-testid="estimate-edits-count">{items.length}</span>
      </button>
      {open && (
        <>
          <p className="mb-3 mt-2 text-xs text-muted-foreground">
            {t("Every time a manager changed the estimated minutes on a room or scheduled task — who, when, why and what it was changed from and to.")}
          </p>
          {items.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground" data-testid="estimate-edits-empty">{t("No estimate corrections in this range.")}</p>
          ) : (
            <div className="divide-y divide-border">
              {items.map((m, i) => (
                <div key={`${m.clean_id}-${m.at}`} className="flex items-start justify-between gap-3 py-2.5 text-sm" data-testid={`estimate-edit-row-${i}`}>
                  <div className="min-w-0">
                    <span className="font-display font-extrabold">{m.is_task ? m.label : t("Room {{room}}", { room: m.label })}</span>
                    <span className="ml-2 text-muted-foreground">{fmtDMY(m.date)}</span>
                    <div className="text-muted-foreground">
                      {fmtMins(m.from)} → <b className="text-foreground">{fmtMins(m.to)}</b>
                      {m.actual_minutes !== null && <span> · {t("actual {{n}} min", { n: m.actual_minutes })}</span>}
                      {m.definition_updated && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-semibold text-indigo-800"><Repeat className="h-3 w-3" /> {t("standing estimate updated")}</span>}
                    </div>
                    {m.reason && <div className="text-xs italic text-muted-foreground" data-testid={`estimate-edit-reason-${i}`}>“{m.reason}”</div>}
                  </div>
                  <div className="shrink-0 text-right text-xs text-muted-foreground">
                    <div className="font-semibold text-foreground">{m.by_name}</div>
                    <div>{fmtDMY(m.at)} · {fmtTime(m.at)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Card>
  );
}
